// Agent name tags: uppercase mono pills above each agent (floor view), with a lead star and a live
// status dot, positioned every frame from scene.getAnchors() (transform only). Click → agent drawer.
import { h, setText, setAttr } from '../dom.js';
import { t } from '../i18n/fr.js';
import { normalizeStatus } from '../store.js';
import { cardPosition } from './deptcards.js';

const STATUSES = ['idle', 'working', 'waiting', 'error', 'done'];
/** Names longer than this get the tighter `is-long` tag (names go up to 22 characters). */
export const LONG_NAME = 14;

/**
 * Creates the name tags controller.
 * @param {object} ctx app context
 * @param {{layer: HTMLElement, onAgentClick: (agentId: string) => void,
 *   onHover?: (target: {kind: string, id: string}|null) => void}} opts
 * @returns {{render(): void, update(anchors: object[]): void, destroy(): void}}
 */
export function createNameTags(ctx, { layer, onAgentClick, onHover }) {
  const { store } = ctx;
  const tags = new Map();

  function build(a) {
    const dot = h('span', { class: 'nt-dot', 'aria-hidden': 'true' });
    const name = h('span', { class: 'nt-name' }, a.name);
    // Focusable (keyboard users reach agents here); inert while its anchor is off screen.
    const el = h('button', {
      type: 'button', class: 'name-tag is-pending', dataset: { agent: a.id },
      onClick: () => onAgentClick?.(a.id),
      onMouseenter: () => onHover?.({ kind: 'agent', id: a.id }),
      onMouseleave: () => onHover?.(null),
      onFocus: () => onHover?.({ kind: 'agent', id: a.id }),
      onBlur: () => onHover?.(null),
    }, dot, a.lead ? h('span', { class: 'nt-star', 'aria-hidden': 'true' }, '★') : null, name);
    return { id: a.id, el, dot, name, x: NaN, y: NaN, visible: null, status: null };
  }

  function fill(tag, a, s) {
    const st = normalizeStatus(s.agentStatus.get(a.id) || a.status);
    const status = STATUSES.includes(st.status) ? st.status : 'idle';
    setText(tag.name, a.name);
    if (status !== tag.status) {
      tag.status = status;
      for (const st of STATUSES) tag.el.classList.toggle(`is-${st}`, st === status);
    }
    tag.el.classList.toggle('is-lead', !!a.lead);
    // Longer than the old 14-character limit: set tighter (web/styles/scene.css .name-tag.is-long).
    tag.el.classList.toggle('is-long', [...String(a.name || '')].length > LONG_NAME);
    const label = t('agent.tagAria', { name: a.name, role: a.role || '', status: t(`agentStatus.${status}`) });
    setAttr(tag.el, 'aria-label', label);
    setAttr(tag.el, 'title', st.activity && status === 'working' ? `${label} · ${st.activity}` : label);
  }

  function render() {
    const s = store.get();
    const agents = s.ui.view === 'building' || s.ui.sceneFallback ? [] : s.office.agents.filter((a) => a.floorId === s.ui.floorId);
    const keep = new Set();
    for (const a of agents) {
      let tag = tags.get(a.id);
      if (!tag) {
        tag = build(a);
        tags.set(a.id, tag);
        layer.appendChild(tag.el);
      }
      fill(tag, a, s);
      keep.add(a.id);
    }
    for (const [id, tag] of tags) if (!keep.has(id)) { tag.el.remove(); tags.delete(id); }
  }

  function applyHover() {
    const hv = store.get().ui.hoverId;
    for (const tag of tags.values()) tag.el.classList.toggle('is-hover', !!hv && hv.kind === 'agent' && hv.id === tag.id);
  }

  function update(anchors) {
    if (!tags.size) return;
    for (const a of anchors) {
      if (a.kind !== 'agent') continue;
      const tag = tags.get(a.id);
      if (!tag) continue;
      const pos = cardPosition(a);
      if (pos.visible !== tag.visible) {
        tag.visible = pos.visible;
        tag.el.classList.toggle('is-offscreen', !pos.visible);
        tag.el.classList.remove('is-pending');
        tag.el.inert = !pos.visible;
      }
      if (!pos.visible || (pos.x === tag.x && pos.y === tag.y)) continue;
      tag.x = pos.x;
      tag.y = pos.y;
      tag.el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
    }
  }

  const offs = [
    store.on('office', render),
    store.on('agentStatus', render),
    store.on('ui', (ui, state, prev) => {
      const p = prev?.ui || {};
      if (ui.view !== p.view || ui.floorId !== p.floorId || ui.sceneFallback !== p.sceneFallback) render();
      else if (ui.hoverId !== p.hoverId) applyHover();
    }),
  ];
  render();
  return { render, update, destroy() { for (const off of offs) off(); for (const tg of tags.values()) tg.el.remove(); tags.clear(); } };
}
